import { useEffect, useState } from "react";
import PanelDecisions from "./panels/PanelDecisions";
import PanelActions from "./panels/PanelActions";
import PanelAwaiting from "./panels/PanelAwaiting";
import PanelProjectsKanban from "./panels/PanelProjectsKanban";
import PanelProductWork from "./panels/PanelProductWork";
import PanelDiscrepancies from "./panels/PanelDiscrepancies";
import PanelCostByTask from "./panels/PanelCostByTask";
import Panel365 from "./panels/Panel365";
import WallClock from "./atoms/WallClock";
import { useDashboard } from "./hooks/useDashboard";
import { handleCallback, decodeIdTokenClaims, signIn } from "./auth/cognito";

function TopBar() {
  return (
    <header className="topbar" role="banner">
      <div className="tb-left">
        <span className="tb-mark" aria-hidden="true" />
        <span className="tb-name">Coding Dashboard</span>
        <span className="tb-ver">v2 · MVP</span>
      </div>
      <div className="tb-centre">
        <WallClock />
      </div>
      <div className="tb-right">
        <span className="tb-who">
          <span className="tb-who-k">signed in</span>
          <span className="tb-who-v">Duncan</span>
        </span>
      </div>
    </header>
  );
}

function ErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const [showDebug, setShowDebug] = useState(false);
  const claims = showDebug ? decodeIdTokenClaims() : null;
  const debugSummary = claims
    ? {
        iss: claims["iss"],
        aud: claims["aud"],
        token_use: claims["token_use"],
        sub: claims["sub"],
        exp: claims["exp"],
        client_id: claims["client_id"],
        cognito_username: claims["cognito:username"],
      }
    : null;
  return (
    <div
      role="alert"
      style={{
        background: "#7f1d1d",
        color: "#fef2f2",
        padding: "12px 16px",
        margin: "12px 16px",
        borderRadius: 6,
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      <strong style={{ display: "block", marginBottom: 4 }}>
        Dashboard cannot reach API.
      </strong>
      <span>{message}</span>
      <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            style={{
              background: "#fef2f2",
              color: "#7f1d1d",
              border: "none",
              padding: "4px 10px",
              borderRadius: 4,
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            Sign in again
          </button>
        )}
        <button
          type="button"
          onClick={() => setShowDebug((v) => !v)}
          style={{
            background: "#fef2f2",
            color: "#7f1d1d",
            border: "none",
            padding: "4px 10px",
            borderRadius: 4,
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {showDebug ? "Hide debug" : "Debug (decode JWT)"}
        </button>
      </div>
      {showDebug && (
        <pre
          style={{
            marginTop: 8,
            padding: 8,
            background: "#450a0a",
            borderRadius: 4,
            overflow: "auto",
            fontSize: 12,
          }}
        >
          {claims
            ? JSON.stringify(debugSummary, null, 2)
            : "No ID token in sessionStorage. Reload and sign in first."}
        </pre>
      )}
    </div>
  );
}

function CallbackHandler() {
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    handleCallback().catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);
  if (error) {
    return (
      <ErrorBanner
        message={`Sign-in failed: ${error}`}
        onRetry={() => {
          void signIn();
        }}
      />
    );
  }
  return (
    <div style={{ padding: 24, fontFamily: "system-ui, sans-serif" }}>
      Completing sign in...
    </div>
  );
}

function Dashboard() {
  const {
    decisions,
    projects,
    entities,
    awaiting,
    recentlyDecided,
    needsDiscussion,
    actions,
    recentlyDoneActions,
    parkedActions,
    discrepancies,
    recentlyDeclinedActions,
    thread365,
    costByTask,
    workByProduct,
    error,
    ratify,
    respondAction,
    dismissCard,
    fetchDecisionBody,
    sendInstruction,
  } = useDashboard();
  // S44 fix #5: only render decision-request escalations in the active panel.
  // Older v2 envelope subtypes (pushback / blocked / query / stuck) had no
  // choices array and rendered with empty A/B/C buttons. Filter on either
  // an explicit subtype or the presence of a decision_state field (older
  // bodies that lack subtype but were authored as decisions).
  const activeDecisions = decisions.filter(
    (d) =>
      (d.choices && d.choices.length > 0) ||
      d.subtype === "decision-request" ||
      (d.decision_state ?? "") !== "",
  );
  return (
    <div className="app">
      <TopBar />
      {error && <ErrorBanner message={error} />}
      <main className="stack" role="main">
        <PanelDecisions
          decisions={activeDecisions}
          onRatify={ratify}
          recentlyDecided={recentlyDecided}
          needsDiscussion={needsDiscussion}
          fetchDecisionBody={fetchDecisionBody}
          onDismiss={dismissCard}
        />
        <PanelActions
          actions={actions}
          onRespond={respondAction}
          recentlyDone={recentlyDoneActions}
          parked={parkedActions}
          recentlyDeclined={recentlyDeclinedActions}
          onDismiss={dismissCard}
        />
        <PanelDiscrepancies discrepancies={discrepancies} />
        <PanelCostByTask costByTask={costByTask} />
        <PanelAwaiting awaiting={awaiting} />
        {/* BOARDROOM-2026-08-08-135707: live dispatch/completion-derived
            per-product view. Positioned ahead of the legacy project-row
            kanban -- it is the primary "what's happening now" signal;
            PanelProjectsKanban is kept for WU-level token/ETA detail. */}
        <PanelProductWork workByProduct={workByProduct} />
        <PanelProjectsKanban projects={projects} entities={entities} />
        <Panel365 thread365={thread365} sendInstruction={sendInstruction} />
      </main>
    </div>
  );
}

export default function App() {
  if (window.location.pathname === "/auth/callback") {
    return <CallbackHandler />;
  }
  return <Dashboard />;
}
